// Sourcing de Transportadoras (PO1-2437 / Epic PO1-2434).
//
// O embarcador pesquisa por destino (UF obrigatória, cidade opcional) e vê as
// transportadoras que comprovadamente atendem a região, com base no histórico
// de CT-e agregado pelo backend (GET /sourcing/transportadoras, lê Redis).
//
// Decisões do épico refletidas aqui:
//  - Sem frete e sem data na tela: só fantasia, razão social e CNPJ (a API já
//    omite contagens/ultimaEntrega por contrato).
//  - A lista renderiza EXATAMENTE na ordem da API — a relevância (recência
//    oculta) vem calculada do backend; não reordenar no front.
//  - "Ver contatos": só contatos do tipo Cotação (canal de cotação spot),
//    cross-tenant por decisão do épico (2026-06-12).
//  - "Atualizar base": dispara o mesmo job do cron (POST /sourcing/refresh) e
//    acompanha até concluir — útil enquanto o agendamento não roda/acabou de
//    subir ambiente.
//
// Padrão visual: hero do padrão novo (espelha AuditoriaCTe), resultado dentro
// do container inset padrão, modal no padrão modal-align-top + fade={false}.
//
// PO1-3648 / PO1-3650 (épico PO1-3359, Rede Everlog):
//  - origem opcional (UF/cidade) → idEstadoOrigem/idCidadeOrigem filtram a
//    cobertura DECLARADA da Rede (a fonte CT-e não muda);
//  - cada item vem com `fonte` ('cte'|'rede'), `temTabela` e `temApi`: selos
//    "Rede Everlog · cobertura declarada", "Tem tabela" (abre a lista com
//    download) e "Tem API";
//  - filtros "Só Rede", "Tem tabela" e "Tem API" são feitos aqui, sobre o
//    resultado, sem reordenar (CT-e primeiro, Rede depois, como a API manda).

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Table,
  Modal,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import api from "services/api";
import {
  autopilotCardStyle,
  autopilotHeaderStyle,
  autopilotBodyStyle,
  autopilotEyebrowStyle,
  autopilotTitleStyle,
  autopilotDescriptionStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
} from "components/autopilot/styles";

const heroHeaderStyle = {
  ...autopilotHeaderStyle,
  padding: "1.65rem 1.65rem 1.45rem",
};

const heroEyebrowStyle = {
  ...autopilotEyebrowStyle,
  marginBottom: "0.6rem",
};

const heroTitleStyle = {
  ...autopilotTitleStyle,
  margin: "0 0 0.55rem",
  fontSize: "2rem",
  fontWeight: 800,
  lineHeight: 1.15,
};

const heroDescriptionStyle = {
  ...autopilotDescriptionStyle,
  maxWidth: 820,
  marginBottom: 0,
  fontSize: "0.98rem",
  lineHeight: 1.65,
};

// Container padrão das telas novas para o miolo do resultado.
const resultInsetStyle = {
  ...autopilotInsetCardStyle,
  padding: "1.1rem 1.2rem",
};

const REFRESH_POLL_MS = 3000;
const REFRESH_POLL_MAX = 40; // ~2min de acompanhamento

// Selects com menu PORTALADO pro body (senão o dropdown fica clipado atrás do
// card seguinte) e visual via tokens de tema — tokens vivem no :root, então o
// menu herda o tema certo mesmo renderizando fora do painel. Mesmo padrão dos
// Indicadores (Emissões/Faturamento).
const selectStyles = {
  menu: (base) => ({
    ...base,
    zIndex: 30,
    background: "var(--everlog-card-surface-flat)",
    border: "1px solid var(--everlog-card-border)",
    borderRadius: 14,
    boxShadow: "var(--everlog-card-shadow)",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
  control: (base, state) => ({
    ...base,
    background: "var(--everlog-card-surface-field)",
    borderColor: state.isFocused
      ? "var(--theme-accent)"
      : "var(--everlog-card-border)",
    borderRadius: 14,
    boxShadow: state.isFocused ? "0 0 0 3px var(--theme-accent-glow)" : "none",
    minHeight: 42,
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--everlog-card-title)",
  }),
  input: (base) => ({
    ...base,
    color: "var(--everlog-card-text)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused
      ? "var(--everlog-card-interactive-bg)"
      : state.isSelected
        ? "var(--everlog-card-surface-active)"
        : "var(--everlog-card-surface-flat)",
    color: "var(--everlog-card-text)",
    cursor: "pointer",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "var(--everlog-card-border)",
  }),
};

// Selos da Rede (PO1-3648/3650): pílulas por token, mesmo desenho do lf-badge
// dos Leilões, sem depender do CSS de lá.
const seloBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  borderRadius: 999,
  padding: "3px 9px",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.3px",
  whiteSpace: "nowrap",
  marginRight: 6,
  marginBottom: 2,
};
const SELO_TONS = {
  warning: { background: "color-mix(in srgb, var(--everlog-status-warning) 18%, transparent)", color: "var(--everlog-status-warning)" },
  info: { background: "color-mix(in srgb, var(--everlog-status-info) 18%, transparent)", color: "var(--everlog-status-info)" },
  success: { background: "color-mix(in srgb, var(--everlog-status-success) 18%, transparent)", color: "var(--everlog-status-success)" },
};
function Selo({ tom, title, onClick, children }) {
  const estilo = { ...seloBase, ...(SELO_TONS[tom] || SELO_TONS.info) };
  if (onClick) {
    return (
      <button type="button" title={title} onClick={onClick} style={{ ...estilo, border: "none", cursor: "pointer" }}>
        {children}
      </button>
    );
  }
  return (
    <span title={title} style={estilo}>
      {children}
    </span>
  );
}

const ehRede = (t) => String((t && t.fonte) || "").toLowerCase() === "rede";

// Filtros no front sobre o resultado (PO1-3650): não reordena.
export function filtrarTransportadoras(lista, filtros) {
  const f = filtros || {};
  return (lista || []).filter((t) => {
    if (f.soRede && !ehRede(t)) return false;
    if (f.temTabela && !t.temTabela) return false;
    if (f.temApi && !t.temApi) return false;
    return true;
  });
}

const FILTROS_VAZIOS = { soRede: false, temTabela: false, temApi: false };
const FILTROS_UI = [
  { chave: "soRede", rotulo: "Só Rede Everlog" },
  { chave: "temTabela", rotulo: "Tem tabela" },
  { chave: "temApi", rotulo: "Tem API" },
];

// CNPJ_CPF vem cru do banco; máscara local (14 → CNPJ, 11 → CPF, resto cru).
function formatCnpjCpf(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 14) {
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5",
    );
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  }
  return value || "—";
}

// Modal "Ver contatos" — padrão das telas novas (modal-align-top, fade={false},
// header customizado; nunca <ModalHeader>). Busca on-demand os contatos de
// cotação da transportadora.
function ContatosModal({ transportadora, onClose }) {
  const [loading, setLoading] = useState(false);
  const [contatos, setContatos] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!transportadora) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setContatos([]);
    api
      .get(`/sourcing/transportadoras/${transportadora.idTransportadora}/contatos`)
      .then((res) => {
        if (!cancelled) setContatos((res.data && res.data.contatos) || []);
      })
      .catch(() => {
        if (!cancelled) setError("Não foi possível carregar os contatos.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transportadora]);

  if (!transportadora) return null;

  return (
    <Modal
      isOpen
      toggle={onClose}
      size="md"
      modalClassName="modal-align-top"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--everlog-card-hero-bg)",
          borderBottom: "1px solid var(--everlog-card-border)",
          padding: "16px 20px",
        }}
      >
        <span
          style={{
            color: "var(--everlog-card-title)",
            fontWeight: 600,
            fontSize: 15,
          }}
        >
          Contatos — {transportadora.fantasia || transportadora.razaoSocial}
        </span>
        <button
          type="button"
          className="close"
          aria-label="Fechar"
          onClick={onClose}
          style={{ color: "var(--everlog-card-title)", opacity: 0.8 }}
        >
          ×
        </button>
      </div>
      <div style={{ padding: "18px 20px" }}>
        <div style={{ ...heroDescriptionStyle, marginBottom: "0.9rem" }}>
          {transportadora.razaoSocial} · {formatCnpjCpf(transportadora.cnpj)}
        </div>

        {loading && <div style={heroDescriptionStyle}>Carregando contatos...</div>}

        {!loading && error && (
          <div style={{ ...heroDescriptionStyle, color: "#fd5d93" }}>{error}</div>
        )}

        {!loading && !error && contatos.length === 0 && (
          <div style={heroDescriptionStyle}>
            Nenhum contato de cotação cadastrado para esta transportadora.
          </div>
        )}

        {!loading && !error && contatos.length > 0 && (
          <Table size="sm" responsive style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Telefones</th>
              </tr>
            </thead>
            <tbody>
              {contatos.map((c, idx) => (
                <tr key={`${c.email || c.nome || "c"}-${idx}`}>
                  <td>{c.nome || "—"}</td>
                  <td>
                    {c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : "—"}
                  </td>
                  <td>{c.telefones && c.telefones.length ? c.telefones.join(" · ") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </Modal>
  );
}

// Modal "Tabelas de referência" (PO1-3650): só os anexos que a transportadora
// marcou como visíveis; a URL é pré-assinada (15 min) e abre em nova aba.
function TabelasReferenciaModal({ transportadora, onClose }) {
  const [loading, setLoading] = useState(false);
  const [itens, setItens] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!transportadora) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItens([]);
    api
      .get(`/sourcing/transportadoras/${transportadora.idTransportadora}/tabelas-referencia`)
      .then((res) => {
        if (!cancelled) setItens((res.data && res.data.items) || []);
      })
      .catch(() => {
        if (!cancelled) setError("Não foi possível carregar as tabelas.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transportadora]);

  if (!transportadora) return null;

  const fmtTamanho = (b) => {
    const n = Number(b) || 0;
    return n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };
  const vigencia = (t) => {
    if (!t.vigenciaInicio && !t.vigenciaFim) return "—";
    const f = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "…");
    return `${f(t.vigenciaInicio)} a ${f(t.vigenciaFim)}`;
  };

  return (
    <Modal isOpen toggle={onClose} size="lg" modalClassName="modal-align-top" fade={false}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--everlog-card-hero-bg)",
          borderBottom: "1px solid var(--everlog-card-border)",
          padding: "16px 20px",
        }}
      >
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 }}>
          Tabelas de referência — {transportadora.fantasia || transportadora.razaoSocial}
        </span>
        <button type="button" className="close" aria-label="Fechar" onClick={onClose} style={{ color: "var(--everlog-card-title)", opacity: 0.8 }}>
          ×
        </button>
      </div>
      <div style={{ padding: "18px 20px" }}>
        <div style={{ ...heroDescriptionStyle, marginBottom: "0.9rem" }}>
          Enviadas pela transportadora como referência: não entram em cálculo nem em auditoria. O link vale por 15 minutos.
        </div>
        {loading && <div style={heroDescriptionStyle}>Carregando tabelas...</div>}
        {!loading && error && <div style={{ ...heroDescriptionStyle, color: "#fd5d93" }}>{error}</div>}
        {!loading && !error && itens.length === 0 && <div style={heroDescriptionStyle}>Nenhuma tabela visível no momento.</div>}
        {!loading && !error && itens.length > 0 && (
          <Table size="sm" responsive style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Arquivo</th>
                <th>Vigência</th>
                <th>Observação</th>
                <th style={{ textAlign: "right" }}>Baixar</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.nome}
                    <div style={{ ...heroDescriptionStyle, fontSize: "0.78rem" }}>
                      {String(t.formato || "").toUpperCase()} · {fmtTamanho(t.tamanho)}
                    </div>
                  </td>
                  <td>{vigencia(t)}</td>
                  <td>{t.observacao || "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    {t.url ? (
                      <a className="btn btn-sm btn-outline-info" href={t.url} target="_blank" rel="noopener noreferrer">
                        Baixar
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </Modal>
  );
}

export default function Sourcing() {
  const [estados, setEstados] = useState([]);
  const [ufOption, setUfOption] = useState(null);
  const [cidadeOption, setCidadeOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [contatosDe, setContatosDe] = useState(null);
  const [tabelasDe, setTabelasDe] = useState(null); // PO1-3650
  // PO1-3648: origem opcional (UF/cidade) e filtros no front.
  const [ufOrigemOption, setUfOrigemOption] = useState(null);
  const [cidadeOrigemOption, setCidadeOrigemOption] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const debounceTimerRef = useRef(null);
  const debounceOrigemRef = useRef(null);
  const pollTimerRef = useRef(null);
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  useEffect(() => {
    api
      .get("/frete/estados")
      .then((res) => {
        setEstados(
          Array.isArray(res.data)
            ? res.data.map((e) => ({ value: e.id, label: e.uf, uf: e.uf }))
            : [],
        );
      })
      .catch(() => setEstados([]));
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Autocomplete de cidade: mesmo padrão do wizard de cotação (GET /cidades,
  // debounce 250ms). O id da cidade é o que vai pra busca — nome nunca filtra.
  const loadCidadeOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        if (!ufOption) {
          resolve([]);
          return;
        }
        debounceTimerRef.current = setTimeout(async () => {
          try {
            const res = await api.get("/cidades", {
              params: { uf: ufOption.uf, q: inputValue || "" },
            });
            resolve(
              (res.data || []).map((c) => ({ value: c.id, label: c.nome })),
            );
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    [ufOption],
  );

  // Mesmo autocomplete para a cidade de ORIGEM (PO1-3648), com timer próprio.
  const loadCidadeOrigemOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (debounceOrigemRef.current) {
          clearTimeout(debounceOrigemRef.current);
        }
        if (!ufOrigemOption) {
          resolve([]);
          return;
        }
        debounceOrigemRef.current = setTimeout(async () => {
          try {
            const res = await api.get("/cidades", {
              params: { uf: ufOrigemOption.uf, q: inputValue || "" },
            });
            resolve(
              (res.data || []).map((c) => ({ value: c.id, label: c.nome })),
            );
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    [ufOrigemOption],
  );

  const handleBuscar = useCallback(async () => {
    if (!ufOption) return;
    setLoading(true);
    setError(null);
    try {
      const params = cidadeOption
        ? { idCidade: cidadeOption.value }
        : { idEstado: ufOption.value };
      // Origem opcional: só filtra a cobertura declarada da Rede.
      if (cidadeOrigemOption) params.idCidadeOrigem = cidadeOrigemOption.value;
      else if (ufOrigemOption) params.idEstadoOrigem = ufOrigemOption.value;
      const res = await api.get("/sourcing/transportadoras", { params });
      setResult(res.data);
      setSearched(true);
    } catch (err) {
      setResult(null);
      setSearched(true);
      setError(
        (err.response && err.response.data && err.response.data.message) ||
          "Não foi possível buscar as transportadoras. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  }, [ufOption, cidadeOption, ufOrigemOption, cidadeOrigemOption]);

  // "Atualizar base": dispara o job (mesmo do cron) e acompanha até concluir.
  // 409 = já tem refresh rodando → acompanha o em andamento.
  const handleAtualizarBase = useCallback(async () => {
    setRefreshing(true);
    setRefreshMsg("Atualizando a base de cobertura...");
    let executionId = null;
    try {
      const res = await api.post("/sourcing/refresh");
      executionId = res.data && res.data.executionId;
    } catch (err) {
      const data = err.response && err.response.data;
      if (err.response && err.response.status === 409 && data && data.executionId) {
        executionId = data.executionId;
        setRefreshMsg("Já existe uma atualização em andamento, acompanhando...");
      } else {
        setRefreshing(false);
        setRefreshMsg("Não foi possível iniciar a atualização.");
        return;
      }
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/sourcing/refresh/${executionId}`);
        const status = res.data && res.data.status;
        if (status === "completed") {
          setRefreshing(false);
          setRefreshMsg("Base atualizada.");
          if (ufOption) handleBuscar();
          return;
        }
        if (status === "failed") {
          setRefreshing(false);
          setRefreshMsg("A atualização falhou. Tente novamente.");
          return;
        }
      } catch {
        // erro transitório de polling: tenta de novo até o limite
      }
      if (attempts < REFRESH_POLL_MAX) {
        pollTimerRef.current = setTimeout(poll, REFRESH_POLL_MS);
      } else {
        setRefreshing(false);
        setRefreshMsg("A atualização segue em segundo plano; busque novamente em instantes.");
      }
    };
    pollTimerRef.current = setTimeout(poll, REFRESH_POLL_MS);
  }, [ufOption, handleBuscar]);

  const destinoLabel = cidadeOption
    ? `${cidadeOption.label}/${ufOption ? ufOption.label : ""}`
    : ufOption
      ? ufOption.label
      : "";

  const todas = (result && result.transportadoras) || [];
  const transportadoras = filtrarTransportadoras(todas, filtros);
  const contagem = {
    soRede: todas.filter(ehRede).length,
    temTabela: todas.filter((t) => t.temTabela).length,
    temApi: todas.filter((t) => t.temApi).length,
  };
  const origemLabel = cidadeOrigemOption
    ? `${cidadeOrigemOption.label}/${ufOrigemOption ? ufOrigemOption.label : ""}`
    : ufOrigemOption
      ? ufOrigemOption.label
      : "";

  return (
    <div className="content">
      <Card
        style={{
          ...autopilotCardStyle,
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <CardHeader style={heroHeaderStyle}>
          <Row className="align-items-start">
            <Col>
              <div style={heroEyebrowStyle}>GoBid</div>
              <h2 style={heroTitleStyle}>Sourcing de Transportadoras</h2>
              <div style={heroDescriptionStyle}>
                Descubra transportadoras que atendem a região onde você precisa
                entregar, com base em entregas que realmente aconteceram.
                Escolha o destino e conheça novas opções para a sua operação.
              </div>
            </Col>
            <Col xs="auto">
              <Button
                className="btn-sm"
                style={autopilotSecondaryButtonStyle}
                disabled={refreshing}
                onClick={handleAtualizarBase}
              >
                {refreshing ? "Atualizando..." : "Atualizar base"}
              </Button>
              {refreshMsg && (
                <div style={{ ...heroDescriptionStyle, fontSize: "0.78rem", marginTop: 6 }}>
                  {refreshMsg}
                </div>
              )}
            </Col>
          </Row>
        </CardHeader>
        <CardBody style={autopilotBodyStyle}>
          {/* PO1-3648: origem opcional. Filtra só a Rede (cobertura declarada);
              a fonte CT-e não olha origem. */}
          <Row className="align-items-end" style={{ marginBottom: "0.75rem" }}>
            <Col md="2" sm="4">
              <label className="wizard-label">UF de origem (opcional)</label>
              <Select
                placeholder="UF"
                aria-label="UF de origem"
                options={estados}
                value={ufOrigemOption}
                onChange={(opt) => {
                  setUfOrigemOption(opt || null);
                  setCidadeOrigemOption(null);
                }}
                isClearable
                styles={selectStyles}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </Col>
            <Col md="5" sm="8">
              <label className="wizard-label">
                Cidade de origem (opcional — vazio vale a UF inteira)
              </label>
              <AsyncSelect
                key={ufOrigemOption ? `o-${ufOrigemOption.value}` : "o-no-uf"}
                placeholder={
                  ufOrigemOption ? "Digite para buscar a cidade" : "Escolha a UF de origem antes"
                }
                isDisabled={!ufOrigemOption}
                loadOptions={loadCidadeOrigemOptions}
                defaultOptions={Boolean(ufOrigemOption)}
                value={cidadeOrigemOption}
                onChange={(opt) => setCidadeOrigemOption(opt || null)}
                isClearable
                noOptionsMessage={() => "Nenhuma cidade encontrada"}
                loadingMessage={() => "Buscando..."}
                styles={selectStyles}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </Col>
            <Col md="5" className="mt-2 mt-md-0">
              <div style={{ ...heroDescriptionStyle, fontSize: "0.8rem" }}>
                A origem filtra as transportadoras da Rede Everlog pela cobertura que declararam; quem entrega por CT-e aparece pelo destino, como sempre.
              </div>
            </Col>
          </Row>
          <Row className="align-items-end">
            <Col md="2" sm="4">
              <label className="wizard-label">UF de destino *</label>
              <Select
                placeholder="UF"
                options={estados}
                value={ufOption}
                onChange={(opt) => {
                  setUfOption(opt || null);
                  setCidadeOption(null);
                }}
                isClearable
                styles={selectStyles}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </Col>
            <Col md="5" sm="8">
              <label className="wizard-label">
                Cidade de destino (opcional — vazio busca a UF inteira)
              </label>
              <AsyncSelect
                key={ufOption ? ufOption.value : "no-uf"}
                placeholder={
                  ufOption ? "Digite para buscar a cidade" : "Escolha a UF antes"
                }
                isDisabled={!ufOption}
                loadOptions={loadCidadeOptions}
                defaultOptions={Boolean(ufOption)}
                value={cidadeOption}
                onChange={(opt) => setCidadeOption(opt || null)}
                isClearable
                noOptionsMessage={() => "Nenhuma cidade encontrada"}
                loadingMessage={() => "Buscando..."}
                styles={selectStyles}
                menuPortalTarget={selectMenuPortalTarget}
              />
            </Col>
            <Col md="3" sm="6" className="mt-2 mt-md-0">
              <Button
                color="primary"
                disabled={!ufOption || loading}
                onClick={handleBuscar}
              >
                {loading ? "Buscando..." : "Buscar transportadoras"}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Card externo com o mesmo fundo do hero; o inset interno mantém a cor
          padrão justamente pra dar o contraste (feedback Rodrigo 12/06). */}
      <Card
        style={{
          ...autopilotCardStyle,
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <CardBody style={autopilotBodyStyle}>
          <div style={resultInsetStyle}>
            {!searched && (
              <div style={heroDescriptionStyle}>
                Escolha um destino acima para descobrir quem entrega lá.
              </div>
            )}

            {searched && error && (
              <div style={{ ...heroDescriptionStyle, color: "#fd5d93" }}>
                {error}
              </div>
            )}

            {searched && !error && todas.length === 0 && (
              <div style={heroDescriptionStyle}>
                Nenhuma transportadora encontrada para {destinoLabel || "esse destino"}
                {origemLabel ? ` saindo de ${origemLabel}` : ""}.
                Tente buscar pela UF inteira{origemLabel ? " ou sem a origem" : ""}.
              </div>
            )}

            {searched && !error && todas.length > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.9rem" }}>
                  <div style={{ ...heroDescriptionStyle, marginBottom: 0 }}>
                    <strong>{todas.length}</strong>{" "}
                    {todas.length === 1
                      ? "transportadora atende"
                      : "transportadoras atendem"}{" "}
                    <strong>{destinoLabel}</strong>
                    {origemLabel ? (
                      <>
                        {" "}
                        saindo de <strong>{origemLabel}</strong>
                      </>
                    ) : null}
                    {transportadoras.length !== todas.length ? ` · ${transportadoras.length} com os filtros` : ""}
                  </div>
                  {/* PO1-3650: filtros no front sobre o resultado. */}
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }} aria-label="Filtros do resultado">
                    {FILTROS_UI.map((f) => (
                      <Button
                        key={f.chave}
                        size="sm"
                        color="info"
                        outline={!filtros[f.chave]}
                        aria-pressed={Boolean(filtros[f.chave])}
                        onClick={() => setFiltros((cur) => ({ ...cur, [f.chave]: !cur[f.chave] }))}
                      >
                        {f.rotulo} ({contagem[f.chave]})
                      </Button>
                    ))}
                  </div>
                </div>
                {transportadoras.length === 0 ? (
                  <div style={heroDescriptionStyle}>Nenhuma transportadora com esses filtros.</div>
                ) : (
                  <Table size="sm" responsive style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th>Nome Fantasia</th>
                        <th>Razão Social</th>
                        <th>CNPJ</th>
                        <th>Selos</th>
                        <th style={{ width: 140, textAlign: "right" }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transportadoras.map((t) => (
                        <tr key={t.idTransportadora} data-fonte={t.fonte || "cte"}>
                          <td>{t.fantasia || "—"}</td>
                          <td>{t.razaoSocial || "—"}</td>
                          <td>{formatCnpjCpf(t.cnpj)}</td>
                          <td>
                            {ehRede(t) ? (
                              <Selo tom="warning" title="Entrou pela Rede Everlog e declarou que atende esta rota; sem histórico de CT-e com você.">
                                Rede Everlog · cobertura declarada
                              </Selo>
                            ) : null}
                            {t.temTabela ? (
                              <Selo tom="info" title="A transportadora enviou uma tabela de referência. Clique para ver e baixar." onClick={() => setTabelasDe(t)}>
                                Tem tabela
                              </Selo>
                            ) : null}
                            {t.temApi ? (
                              <Selo tom="success" title="A transportadora declarou que cota por API.">
                                Tem API
                              </Selo>
                            ) : null}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Button
                              className="btn-sm"
                              style={autopilotSecondaryButtonStyle}
                              onClick={() => setContatosDe(t)}
                            >
                              Ver contatos
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <ContatosModal
        transportadora={contatosDe}
        onClose={() => setContatosDe(null)}
      />
      <TabelasReferenciaModal
        transportadora={tabelasDe}
        onClose={() => setTabelasDe(null)}
      />
    </div>
  );
}
